// @flow
import queryString from 'query-string';
import { isTrue } from 'utils/varValidation';

// Constants
import { APP_NAMES } from 'constants/appName';

const { STATUS_TRACKER, SELF_CHECK_IN, CONSUMER_8 } = APP_NAMES;
const EXPERIMENTS_FLAG = '$experiments';

type Options = {|
  /**
   * True to treat the keys as case insensitive
   */
  keysCaseInsensitive: boolean
|};

/**
 * A singleton that represents the query string and its parameters,
 * it's a mutable entity that isn't syncronized with location.search upon
 * modification
 *
 * @example
 * // initialization
 * import 'store/entities/QueryString'
 *
 * // usage
 * import qs from 'store/entities/QueryString'
 * qs.get()
 */
class QueryString {
  options: Options;
  original: { [string]: string };
  parsed: { [string]: string };
  mutable: { [string]: string };

  constructor(
    search: string,
    options: Options = {
      keysCaseInsensitive: true
    }
  ) {
    this.options = options;

    let parsed = queryString.parse(search);

    /**
     * Original query params
     */
    this.original = Object.freeze(parsed);

    // transform keys to lowercase
    if (this.options.keysCaseInsensitive) {
      const transformed = {};
      Object.keys(parsed).forEach((key: string) => {
        transformed[key.toLowerCase()] = parsed[key];
      });
      parsed = transformed;
    }

    /**
     * Params that went through a chain of transformations
     */
    this.parsed = parsed;

    /**
     * Mutable parsed query params
     */
    this.mutable = { ...parsed };
  }

  /**
   * Applies transformations to the keys according to options
   */
  transformKey(key: string) {
    if (this.options.keysCaseInsensitive) {
      return key.toLowerCase();
    }
    return key;
  }

  // public api

  get(k: string): string | void {
    const key = this.transformKey(k);
    return this.mutable[key];
  }

  has(k: string): boolean {
    const key = this.transformKey(k);
    return key in this.mutable;
  }

  set(k: string, v: string) {
    const key = this.transformKey(k);
    this.mutable[key] = v;
  }

  /**
   * Tests the query param `k` to check if it's boolean
   */
  isBoolean(k: string) {
    const key = this.transformKey(k);
    return this.getBoolean(key) === 'true' || this.getBoolean(key) === 'false';
  }

  /**
   * Gets a "boolean" value, "boolean" values are `true` whenever
   * the query string value is strictly equal to the string "true"
   */
  getBoolean(k: string) {
    const key = this.transformKey(k);
    return this.get(key) === 'true';
  }

  /**
   * Quick alias to get the webkey
   */
  getWebkey() {
    const webkey = this.get('webkey');
    if (webkey) {
      return webkey.split(':')[0];
    }
  }

  /**
   * Quick alias to get Application Name
   */
  getAppName() {
    if (isTrue(this.get(STATUS_TRACKER))) {
      return STATUS_TRACKER;
    } else if (isTrue(this.get(SELF_CHECK_IN))) {
      return SELF_CHECK_IN;
    } else {
      return CONSUMER_8;
    }
  }

  /**
   * Restores mutable params to the original values
   */
  restoreOriginal() {
    this.mutable = { ...this.original };
  }

  /**
   * Restores mutable params to the parsed values
   */
  restoreParsed() {
    this.mutable = { ...this.parsed };
  }

  stringify(opts: any) {
    return queryString.stringify(this.mutable, opts);
  }
}

const instance = new QueryString(window.location.search);
export default instance;
export { QueryString, EXPERIMENTS_FLAG };



// WEBPACK FOOTER //
// ./src/store/entities/QueryString.js